import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ResendOtpComponent } from '../resend-otp/resend-otp.component';

@Component({
    selector: 'app-otp-field',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ResendOtpComponent],
    templateUrl: './otp-field.component.html',
    styleUrl: './otp-field.component.css'
})
export class OtpFieldComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() workflowData: any;
  @Input() autoFocused: any;
  @Output() otpCompleted = new EventEmitter<{ componentId: string }>();
  @ViewChild('focusInput') focusInput!: ElementRef<HTMLInputElement>;
  errorMessage: string = '';
  validationRules: any[] = [];
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.validationRules = this.fields.validationRules;
    const otpLength = this.fields?.properties?.otpLength || 6;
    const existingOtpArray = this.formGroup.get('otp') as FormArray;
    if (existingOtpArray && existingOtpArray instanceof FormArray) {
      if (existingOtpArray.length === 0) {
        for (let i = 0; i < otpLength; i++) {
          existingOtpArray.push(
            this.fb.control('', [
              Validators.required,
              Validators.pattern('[0-9]'),
            ]),
          );
        }
      }
    }
    this.errorMessage = '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.autoFocused) return;

    setTimeout(() => {
      this.focusInput?.nativeElement.focus();
    });
    if (changes['fields'] && this.fields?.validationRules?.length) {
      this.validationRules = [...this.fields?.validationRules];
    }
  }

  get otpArray(): FormArray {
    return this.formGroup.get('otpArray') as FormArray;
  }

  /** Function to get the style from the json for input field */
  getInputStyle(index: number): any {
    const control = this.otpArray.at(index);
    const hasError = control.invalid && control.touched;
    if (hasError) return this.fields?.properties?.errorStyle || {};
    return this.fields?.properties?.inputStyle || {};
  }

  /** Function to store the entered otp in otpCode formControlName */
  onOtpInput(index: number): void {
    const input = this.otpArray.at(index);
    const value = input.value;

    if (value && /^[0-9]$/.test(value) && index < this.otpArray.length - 1) {
      const nextInput = document.querySelectorAll('input[type="text"]')[
        index + 1
      ] as HTMLElement;
      nextInput?.focus();
    }

    const otpString = this.otpArray.value.join('');
    this.formGroup.get('otp')?.setValue(otpString);
    if (otpString && otpString.length > 0) {
      this.errorMessage = '';
      this.cdr.detectChanges();
    }
    const allFilled = this.otpArray.controls.every(
      (ctrl) => ctrl.value?.length === 1,
    );
    if (allFilled) {
      this.validateOtp();
      this.otpCompleted.emit({ componentId: 'submitButton' });
    }
  }

  /** On backspace remove the current number from the input and move to the previous input */
  onOtpKeyDown(event: KeyboardEvent, index: number): void {
    const key = event.key;
    const control = this.otpArray.at(index);

    if (key === 'Backspace') {
      if (control.value) {
        control.setValue('');
      } else if (index > 0) {
        const prevInput = document.querySelectorAll('input[type="text"]')[
          index - 1
        ] as HTMLElement;
        prevInput?.focus();
        this.otpArray.at(index - 1).setValue('');
      }
      event.preventDefault();
    }
  }

  /** Copy the OTP from the email and directly paste in the input */
  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').split('');

    // Apply digits to otpArray
    digits.slice(0, this.otpArray.length).forEach((digit, index) => {
      const ctrl = this.otpArray.at(index);
      ctrl.setValue(digit);
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity();
    });

    // Combine and patch to 'otp' field
    const otpValue = this.otpArray.controls.map((ctrl) => ctrl.value).join('');
    this.formGroup.get('otp')?.setValue(otpValue);
    if (otpValue && otpValue.length > 0) {
      this.errorMessage = '';
      this.cdr.detectChanges();
    }
    // Auto-submit if needed
    if (digits.length >= this.otpArray.length) {
      this.validateOtp();
      this.otpCompleted.emit({ componentId: 'submitButton' });
    }
  }

  /** Function to validate the otp which is entered */
  validateOtp(): void {
    const otpArray = this.otpArray.value;
    const otp = otpArray.join('');
    this.errorMessage = '';
    if (!otp) {
      this.errorMessage = 'You must enter OTP';
      this.cdr.detectChanges();
      return;
    }
    if (this.validationRules) {
      for (let rule of this.validationRules) {
        if (rule.type === 'minLength' && otp.length < rule.value) {
          this.errorMessage = rule.error;
          return;
        }
        if (rule.type === 'maxLength' && otp.length > rule.value) {
          this.errorMessage = rule.error;
          return;
        }
        if (rule.type === 'regex' && !new RegExp(rule.value).test(otp)) {
          this.errorMessage = rule.error;
          return;
        }
      }
    }
  }
}
